import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import BankFields from "@/components/BankFields";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, Save } from "lucide-react";

export default function DistBank() {
  const [b, setB] = useState({
    bank_name: "", bank_name_other: "", bank_account: "",
    ifsc: "", account_holder: "", branch: "",
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/dist/bank").then((r) => { if (r.data) { setB((p) => ({ ...p, ...r.data })); setSaved(true); } }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // If user chose "Other", ship the manual value as the actual bank_name
      const payload = { ...b };
      if (payload.bank_name?.startsWith("Other") && payload.bank_name_other) {
        payload.bank_name = payload.bank_name_other.trim();
      }
      delete payload.bank_name_other;
      await api.post("/dist/bank", payload);
      toast.success("Bank details saved");
      setSaved(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DashboardLayout title="Bank Details" subtitle="Where your payouts will land">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 bg-white border border-line p-6 lg:p-8 rounded-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 grid place-items-center bg-royal/[0.06] border border-royal/20 rounded-sm">
              <CreditCard size={20} className="text-royal" />
            </div>
            <div>
              <h3 className="font-serif text-xl text-royal">Registered Bank Account</h3>
              <p className="text-xs text-navy/60 mt-0.5">Payouts are processed weekly to this account after KYC approval.</p>
            </div>
          </div>

          <BankFields values={b} onChange={setB} required testidPrefix="bank" />

          <button
            data-testid="bank-submit"
            disabled={busy}
            className="mt-6 bg-royal text-white px-6 py-3 hover:bg-royal-deep rounded-sm disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Save size={16} /> {busy ? "Saving..." : saved ? "Update Details" : "Save Bank Details"}
          </button>
        </form>

        <aside className="bg-white border border-line p-6 rounded-sm h-fit">
          <ShieldCheck className="text-gold-deep" size={22} />
          <h4 className="font-serif text-lg text-royal mt-3">Payout safety</h4>
          <ul className="mt-4 space-y-3 text-xs text-navy/70 leading-relaxed">
            <li>• Bank details are validated at first payout attempt.</li>
            <li>• Any change resets your next payout to a 48-hour hold.</li>
            <li>• Ensure the name matches your PAN — mismatches will fail.</li>
            <li>• Never share your account number publicly — the admin will never ask.</li>
            <li>• IFSC is verified against a live public bank registry — bank + branch auto-fill when the code is valid.</li>
          </ul>
        </aside>
      </div>
    </DashboardLayout>
  );
}
